import { useEffect } from "react";
import PhotoState from "src/api/graphql/__generated__/enums/PhotoState";
import { useGatheringContext } from "src/contexts/GatheringContext";
import { GatheringOrLocalPhoto } from "src/hooks/useGatheringPhotosWithLocal";

/**
 * Updates `previewPhotos` in the gathering store with the first `numPreviewPhotos` valid photos.
 */
const useSetPreviewPhotos = ({
	photos,
	numPreviewPhotos = 14,
}: {
	photos: GatheringOrLocalPhoto[];
	/**
	 * @default 14
	 */
	numPreviewPhotos?: number;
}) => {
	const { store } = useGatheringContext();

	useEffect(() => {
		const { previewPhotos } = store.getState();
		if (previewPhotos.length >= numPreviewPhotos) {
			return;
		}

		const newPreviewPhotos: GatheringOrLocalPhoto[] = [];
		const validPhotos = photos.filter((photo) => {
			return photo.state === PhotoState.Active && photo.readUrl?.v0TemplateUrl;
		});
		const validPhotoIds = new Set<string>(validPhotos.map((photo) => photo.photoId));

		for (const oldPreviewPhoto of previewPhotos) {
			if (!validPhotoIds.has(oldPreviewPhoto.photoId)) {
				continue;
			}
			newPreviewPhotos.push(oldPreviewPhoto);
		}
		let i = 0;
		while (newPreviewPhotos.length < numPreviewPhotos && i < validPhotos.length) {
			const photoCandidate = validPhotos[i++];
			if (
				photoCandidate &&
				!newPreviewPhotos.some((photo) => photo.photoId === photoCandidate.photoId)
			) {
				newPreviewPhotos.push(photoCandidate);
			}
		}
		store.setState({ previewPhotos: newPreviewPhotos });
	}, [photos, store, numPreviewPhotos]);
};

export default useSetPreviewPhotos;
